import format from 'date-fns/format/index'

// based on https://gist.github.com/jed/982883
export function uuid() {
	const replace_placeholder = (a: string) => (parseInt(a) ^ Math.random() * 16 >> parseInt(a) / 4).toString(16)
	return `00000000-0000-4000-8000-000000000000`.replace(/[08]/g, replace_placeholder)
}

export const sum = (arr: (number | string)[]) => arr.reduce((s: number, n) => s + Number(n), 0)

export function starts_with(s1: string, s2: string) {
	return s1.slice(0, s2.length) === s2
}

export function capitalize(s: string) {
	return s.slice(0, 1).toUpperCase() + s.slice(1)
}

export function iso_date(date: string | Date) {
	return format(new Date(date), `yyyy-MM-dd`)
}

export function date(date: string | Date) {
	return format(new Date(date), `MMM dd, y`)
}

export function time(date: string | Date) {
	return format(new Date(date), `MMM dd, y, h:mm aa`)
}

export function ensure_array<T>(v: T | T[]): T[] {
	return Array.isArray(v) ? v : (v === undefined ? [] : [ v ])
}

const CARRIER_VALUES = [
	`stamps_com`,
	`ups`,
	`ups_walleted`,
	`dhl_express`,
	`dhl_express_worldwide`,
	`fedex`,
] as const
export type Carrier = typeof CARRIER_VALUES[number]
export function is_carrier(value: string): value is Carrier {
	return CARRIER_VALUES.includes(value as any)
}

export function tracking_url(carrier: Carrier, number: string) {
	const url = {
		stamps_com: `https://tools.usps.com/go/TrackConfirmAction.action?tLabels=${ number }`,
		ups: `https://wwwapps.ups.com/WebTracking?HTMLVersion=5.0&Requester=NES&AgreeToTermsAndConditions=yes&loc=en_US&tracknum=${ number }`,
		ups_walleted: `https://wwwapps.ups.com/WebTracking?HTMLVersion=5.0&Requester=NES&AgreeToTermsAndConditions=yes&loc=en_US&tracknum=${ number }`,
		dhl_express: `https://www.dhl.com/en/express/tracking.html?AWB=${ number }&brand=DHL`,
		dhl_express_worldwide: `https://www.dhl.com/en/express/tracking.html?AWB=${ number }&brand=DHL`,
		fedex: `https://www.fedex.com/fedextrack/?tracknumbers=${ number }&cntry_code=us`,
	}[carrier]

	if (!url) {
		throw new Error(`Unknown carrier "${ carrier }"`)
	}

	return url
}

const carrier_icons = {
	ups: `https://img.icons8.com/windows/64/000000/ups.png`,
	ups_walleted: `https://img.icons8.com/windows/64/000000/ups.png`,
	stamps_com: `https://img.icons8.com/windows/64/000000/usps.png`,
	dhl_express: `https://img.icons8.com/windows/64/000000/dhl.png`,
	dhl_express_worldwide: `https://img.icons8.com/windows/64/000000/dhl.png`,
	fedex: `https://img.icons8.com/windows/64/000000/fedex.png`,
}
export const carrier_icon = (carrier: Carrier) => carrier_icons[carrier]

export function shorten_attribute(value: string) {
	return value && String(value).replace(/\(([^+)]+)\)/, ``).replace(/\((\+[ ]*\$[0-9]+)[^)]*\)/, (m, p) => `(${ p })`).trim()
}

export function thousands_style_chunking(key: string) {
	if (typeof key !== `string`) {
		throw new Error(`gimme a string to chunk`)
	}
	return key.split(``).reduceRight(
		([ current_operating_chunk, ...previously_chunked_chunks ], character, index) =>
			((key.length - index) % 3 === 1
				? [ character, current_operating_chunk, ...previously_chunked_chunks ]
				: ([ character + current_operating_chunk, ...previously_chunked_chunks ])), [ `` ]
	).filter(Boolean)
}

export function sort_attributes(attributes: { [k: string]: string }, product?: null | undefined | { attributes: { name: string }[] }) {
	if (!product) {
		return Object.entries(attributes)
	}
	const sort = product ? product.attributes.map(a => a.name).reverse() : []

	return Object.entries(attributes).sort(([ a ], [ b ]) => sort.indexOf(b) - sort.indexOf(a))
}

export function plural(count: number, singular_noun: string, plural_noun?: string) {
	plural_noun = plural_noun || singular_noun + `s`
	const noun = count === 1 ? singular_noun : plural_noun
	return `${ count } ${ noun }`
}

export const substitute = (template: string, data: { [k: string ]: any }) => template.replace(
	/%([_$\w]*(?:\.[_$\w]*)*)%/g, (_: string, path: string) => (new Function(`data`, `path`, `return data.` + path))(data, path)
)
